// components/OfflineBanner.js
// Offline status banner — shows when offline, syncing, or just came back online

import { Wifi, WifiOff, RefreshCw, X, CloudLightning } from 'lucide-react';

const OfflineBanner = ({ isOnline, queueCount, isSyncing, showBanner, onSync, onDismiss }) => {
  if (!showBanner) return null;

  // Offline banner
  if (!isOnline) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.1))',
        borderBottom: '1px solid rgba(239,68,68,0.3)',
        padding: '0.6rem 1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.85rem',
        zIndex: 9999,
        position: 'relative'
      }}>
        <WifiOff size={16} style={{ color: '#ef4444' }} />
        <span style={{ color: '#fca5a5' }}>
          <strong>You're offline.</strong> Don't worry — you can still view cached data and take notes. Changes will sync when you reconnect.
        </span>
        {queueCount > 0 && (
          <span style={{
            background: 'rgba(239,68,68,0.2)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '12px',
            padding: '0.15rem 0.6rem',
            color: '#fca5a5',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            {queueCount} pending {queueCount === 1 ? 'change' : 'changes'}
          </span>
        )}
        <button onClick={onDismiss} style={{
          position: 'absolute', right: '0.75rem', background: 'none', border: 'none',
          color: '#fca5a5', cursor: 'pointer', padding: '0.25rem', display: 'flex'
        }}>
          <X size={14} />
        </button>
      </div>
    );
  }

  // Back online with pending items
  if (isOnline && queueCount > 0) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(217,119,6,0.1))',
        borderBottom: '1px solid rgba(245,158,11,0.3)',
        padding: '0.6rem 1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.85rem',
        zIndex: 9999,
        position: 'relative'
      }}>
        <CloudLightning size={16} style={{ color: '#f59e0b' }} />
        <span style={{ color: '#fcd34d' }}>
          <strong>Back online!</strong> {queueCount} offline {queueCount === 1 ? 'change needs' : 'changes need'} to sync.
        </span>
        <button onClick={onSync} disabled={isSyncing} style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          border: 'none',
          borderRadius: '6px',
          padding: '0.3rem 0.75rem',
          color: '#0f0f1e',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: 700,
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
          opacity: isSyncing ? 0.6 : 1
        }}>
          <RefreshCw size={12} style={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />
          {isSyncing ? 'Syncing...' : 'Sync Now'}
        </button>
        <button onClick={onDismiss} style={{
          position: 'absolute', right: '0.75rem', background: 'none', border: 'none',
          color: '#fcd34d', cursor: 'pointer', padding: '0.25rem', display: 'flex'
        }}>
          <X size={14} />
        </button>
      </div>
    );
  }

  // Just came back online, all synced
  if (isOnline && queueCount === 0) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.1))',
        borderBottom: '1px solid rgba(16,185,129,0.3)',
        padding: '0.6rem 1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.85rem',
        zIndex: 9999,
        position: 'relative'
      }}>
        <Wifi size={16} style={{ color: '#10b981' }} />
        <span style={{ color: '#6ee7b7' }}>
          <strong>All synced!</strong> You're back online and up to date.
        </span>
        <button onClick={onDismiss} style={{
          position: 'absolute', right: '0.75rem', background: 'none', border: 'none',
          color: '#6ee7b7', cursor: 'pointer', padding: '0.25rem', display: 'flex'
        }}>
          <X size={14} />
        </button>
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
